import { useCallback, useEffect, useRef } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { Loader2, CheckCircle2, XCircle, Ban } from 'lucide-react';
import { Banner } from '@/components/ui/banner';
import { Button } from '@/components/ui/button';
import { useImportProgressStore } from '@/stores/importProgressStore';
import { refreshImportedVoters, useCancelImport } from '@/hooks/queries';

export function ImportBanner() {
  const progress = useImportProgressStore((s) => s.progress);
  const dismissed = useImportProgressStore((s) => s.dismissed);
  const dismiss = useImportProgressStore((s) => s.dismiss);
  const isRegeocode = useImportProgressStore((s) => s.isRegeocode);
  const label = isRegeocode ? 'Re-geocoding voters' : 'Importing voters';
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { mutate: cancelImport, isPending: isCancelling } = useCancelImport();
  const hasInvalidated = useRef<string | null>(null);

  // Invalidate voter queries when import completes (don't wait for dismiss)
  useEffect(() => {
    const jobId = progress?.jobId;
    if (progress?.status === 'Completed' && jobId && hasInvalidated.current !== jobId) {
      hasInvalidated.current = jobId;
      refreshImportedVoters(queryClient);
    }
  }, [progress?.status, progress?.jobId, queryClient]);

  const handleDismiss = useCallback(() => {
    refreshImportedVoters(queryClient);
    dismiss();
  }, [queryClient, dismiss]);

  const handleCancel = useCallback(() => {
    if (progress?.jobId) {
      cancelImport(progress.jobId);
    }
  }, [progress, cancelImport]);

  const handleReviewErrors = useCallback(() => {
    // Voter import (and its error-review dialog) now lives under the settings hub's
    // Campaign preferences sub-page.
    navigate({ to: '/app/settings/campaign-preferences' });
    handleDismiss();
  }, [navigate, handleDismiss]);

  if (!progress || dismissed) return null;

  const pct =
    progress.totalRecords > 0
      ? Math.round((progress.processedRecords / progress.totalRecords) * 100)
      : 0;

  if (progress.status === 'Pending') {
    return (
      <Banner variant="info">
        <Loader2 className="h-4 w-4 animate-spin shrink-0" />
        <span className="text-sm font-medium">
          {isRegeocode ? 'Preparing re-geocode...' : 'Preparing import...'}
        </span>
      </Banner>
    );
  }

  if (progress.status === 'Running') {
    return (
      <Banner variant="info">
        <Loader2 className="h-4 w-4 animate-spin shrink-0" />
        <span className="text-sm font-medium shrink-0">{label} — {pct}%</span>
        <div className="flex-1 max-w-xs bg-white/30 rounded-full h-2 mx-2">
          <div
            className="bg-white h-2 rounded-full transition-all duration-300"
            style={{ width: `${pct}%` }}
          />
        </div>
        <Stats progress={progress} />
        <Button
          size="sm"
          variant="ghost"
          className="text-white hover:bg-white/20 h-7 text-xs"
          onClick={handleCancel}
          disabled={isCancelling}
        >
          {isCancelling ? 'Cancelling...' : 'Cancel'}
        </Button>
      </Banner>
    );
  }

  if (progress.status === 'Completed') {
    return (
      <Banner variant="success">
        <CheckCircle2 className="h-4 w-4 shrink-0" />
        <span className="text-sm font-medium">
          {isRegeocode ? 'Re-geocoding complete' : 'Import complete'}
        </span>
        <Stats progress={progress} />
        {progress.errorCount > 0 && (
          <Button
            size="sm"
            variant="ghost"
            className="text-white hover:bg-white/20 h-7 text-xs"
            onClick={handleReviewErrors}
          >
            Review Errors
          </Button>
        )}
        <Button
          size="sm"
          variant="ghost"
          className="text-white hover:bg-white/20 h-7 text-xs"
          onClick={handleDismiss}
        >
          Dismiss
        </Button>
      </Banner>
    );
  }

  if (progress.status === 'Failed') {
    return (
      <Banner variant="error">
        <XCircle className="h-4 w-4 shrink-0" />
        <span className="text-sm font-medium">
          {isRegeocode ? 'Re-geocoding failed' : 'Import failed'}
        </span>
        {progress.errors.length > 0 && (
          <span className="text-xs opacity-90 truncate max-w-md">
            — {progress.errors[0]}
          </span>
        )}
        <div className="flex-1" />
        <Button
          size="sm"
          variant="ghost"
          className="text-white hover:bg-white/20 h-7 text-xs"
          onClick={handleDismiss}
        >
          Dismiss
        </Button>
      </Banner>
    );
  }

  if (progress.status === 'Cancelled') {
    return (
      <Banner variant="warning">
        <Ban className="h-4 w-4 shrink-0" />
        <span className="text-sm font-medium">
          {isRegeocode ? 'Re-geocoding cancelled' : 'Import cancelled'}
        </span>
        <Stats progress={progress} />
        <Button
          size="sm"
          variant="ghost"
          className="text-white hover:bg-white/20 h-7 text-xs"
          onClick={handleDismiss}
        >
          Dismiss
        </Button>
      </Banner>
    );
  }

  return null;
}

// -- Internal helpers --

function Stats({ progress }: { progress: { importedCount: number; linkedCount?: number; updatedCount?: number; skippedCount: number; errorCount: number } }) {
  return (
    <span className="text-xs opacity-90 shrink-0">
      {progress.importedCount.toLocaleString()} added
      {(progress.updatedCount ?? 0) > 0 && <>, {(progress.updatedCount ?? 0).toLocaleString()} updated</>}
      {(progress.linkedCount ?? 0) > 0 && <>, {(progress.linkedCount ?? 0).toLocaleString()} linked</>}
      {progress.skippedCount > 0 && <>, {progress.skippedCount.toLocaleString()} duplicates</>}
      {progress.errorCount > 0 && <>, {progress.errorCount.toLocaleString()} errors</>}
    </span>
  );
}

